import type { UIMessage } from 'ai';
import type { Unit, ValueKind } from './format';

// Wire contract with assistant/api (mirror of src/agent/protocol.ts and
// src/semantic/types.ts). Every message is an AI SDK UIMessage whose parts
// are text plus these typed data parts.

export type DimensionId =
  | 'property'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'
  | 'day_of_week'
  | 'department'
  | 'position'
  | 'revenue_category'
  | 'gl_department'
  | 'account'
  | 'account_type'
  | 'review_source'
  | 'review_category'
  | 'review_topic'
  | 'vendor'
  | 'invoice'
  | 'item'
  | 'employee'
  | 'shift'
  | 'revenue_code'
  | 'mapped_type'
  | 'document'
  | 'line';

export const TIME_DIMENSIONS: DimensionId[] = ['day', 'week', 'month', 'quarter', 'year', 'day_of_week'];

export type CompareKey = 'budget' | 'ly' | 'prior';

export type ViewType = 'kpi' | 'line' | 'area' | 'column' | 'bar' | 'stacked_column' | 'donut' | 'diverging' | 'table' | 'table_across';

/** How the answer shows a result (assistant/api src/agent/display.ts). */
export interface ResultDisplay {
  mode: 'chart' | 'tiles' | 'table' | 'collapsed';
  /** Citation order; results the answer never cites come last. */
  order: number;
  /** For a result shown as a table: the chart its view switch can still draw. */
  chart?: ViewType;
  /** It repeats this result (same title and columns), so it isn't drawn. */
  repeatOf?: string;
}

export interface ResultColumn {
  key: string;
  label: string;
  role: 'dimension' | 'value';
  dimension?: DimensionId;
  metric?: string;
  compare?: CompareKey;
  kind?: ValueKind;
  unit?: Unit;
  precise?: boolean;
  higherIsBetter?: boolean | null;
  basis?: 'same_store';
}

export interface ResultRow {
  key: string;
  dims: Partial<Record<DimensionId, { key: string | number; label: string }>>;
  values: Record<string, number | null>;
}

export interface ViewSpec {
  type: ViewType;
  x?: DimensionId;
  seriesBy?: DimensionId;
  series: Array<{ column: string; label: string; role: 'primary' | 'comparison' }>;
}

export interface QuerySpec {
  id: string;
  title: string;
  metrics: string[];
  by: DimensionId[];
  period: { preset?: string; start?: string; end?: string };
  compare: Array<'budget' | 'last_year' | 'prior_period'>;
  filters: Record<string, Array<string | number>>;
  sort?: { by: string; direction: 'asc' | 'desc' };
  limit?: number;
  view: string;
}

export interface QueryResult {
  id: string;
  title: string;
  spec: QuerySpec;
  period: { start: string; end: string; label: string; notes: string[] };
  comparisons: Array<{ key: CompareKey; label: string; note: string }>;
  currency: string;
  columns: ResultColumn[];
  rows: ResultRow[];
  totals: Record<string, number | null>;
  others: ResultRow | null;
  rowCount: number;
  truncated: boolean;
  /** Comparisons whose totals cover only `count` of the `of` properties with data this period. */
  sameStore?: Partial<Record<CompareKey, { count: number; of: number }>>;
  notes: string[];
  view: ViewSpec;
  /** The answer's layout for this result; absent while streaming on older services. */
  display?: ResultDisplay;
  sources: Array<{ cube: string; label: string; dataThrough: string | null }>;
  durationMs: number;
  /** find_source_documents: the files, rendered as the source-document card. */
  documents?: SourceDocumentInfo[];
}

/** A file behind the report (mirror of assistant/api src/semantic/types.ts). */
export interface SourceDocumentInfo {
  ref: string;
  propertyId: number;
  propertyName: string | null;
  label: string;
  title: string;
  fileName: string;
  businessDate: string | null;
  receivedAt: string | null;
  /** loaded | not_on_report | failed | processing | received */
  status: string;
  feedsFacts: boolean;
  feeds: string[];
  readable: boolean;
  chars: number | null;
  /** Body for POST /revenueSourceDocuments/file; null when that endpoint doesn't serve the file. */
  open: { stateId: number } | { docId: number } | { importId: number } | null;
}

export interface PlanPart {
  id: string;
  title: string;
  status: 'running' | 'done' | 'error';
  chips: string[];
  rowCount?: number;
  durationMs?: number;
  error?: string;
}

export interface StatusPart {
  phase: 'thinking' | 'querying' | 'writing';
  label: string;
}

export interface AssistantMetadata {
  threadId?: string;
  mode?: 'fast' | 'deep';
  escalated?: boolean;
  createdAt?: string;
  durationMs?: number;
  firstResultMs?: number;
  firstTokenMs?: number;
  dataThrough?: string | null;
  rating?: 1 | -1 | null;
  /** Imported from the v1 assistant. */
  legacy?: boolean;
  /** User messages: files sent with this message (uploaded through the Lodgeic API). */
  attachments?: AttachmentRef[];
  /** User messages: a product button's job, run first (see AssistantSeed). */
  action?: AssistantAction;
  /** User messages: the page it was asked from (sent as ChatBody.context). */
  context?: AssistantPageContext;
  /** User messages: the properties it was asked about, as sent (null = every one). */
  scope?: number[] | null;
}

export interface AttachmentRef {
  id: string;
  fileName: string;
}

/** The governed workflows a product button can run (assistant/api agent/workflowTools.ts WORKFLOWS keys). */
export const WORKFLOW_KEYS = [
  'revenue_budget',
  'forecast_build',
  'budget_review',
  'forecast_accuracy_review',
  'revenue_reforecast',
  'forecast_update',
  'monthly_landing',
  'monthly_close_review',
  'rooms_expense_audit',
  'forecast_driver_mapping',
  'file_apply',
  'room_revenue_import',
  'journal_imbalance',
] as const;

/** A governed workflow a product button runs (assistant/api agent/workflowTools.ts keys). */
export interface AssistantAction {
  workflow: string;
  propertyId: number;
  versionId?: number | null;
  year?: number | null;
}

/**
 * Where the user asked from (mirror of assistant/api src/agent/pageContext.ts):
 * the page, its period and version, the selected row, notes the page hands
 * over. A description for the model — scope is the conversation's
 * locationIds, never this.
 */
export interface AssistantPageContext {
  page?: string;
  label?: string;
  /** The button pressed ("Explain this number"). */
  source?: string;
  period?: { start?: string; end?: string; label?: string; year?: number; month?: number };
  selection?: { kind: string; id?: number; date?: string; label?: string; path?: string[] };
  version?: { id: number; kind?: 'budget' | 'forecast' };
  filters?: Record<string, string | number>;
  propertyIds?: number[];
  /** E.g. the daily briefing card the user clicked into. */
  note?: string;
}

/** Opens the assistant on a new conversation and sends this first (e.g. from Forecast V3). */
export interface AssistantSeed {
  text: string;
  action?: AssistantAction;
  scope?: number[] | null;
  deep?: boolean;
  /** Sent with the first message. */
  context?: AssistantPageContext;
  /** Put the text in the message box for the user to finish instead of sending it. */
  draft?: boolean;
}

export type ChangeStatus = 'proposed' | 'applied' | 'rejected' | 'reverted' | 'failed' | 'stale';

export interface ChangeTotals {
  before: number;
  after: number;
  delta: number;
}

/** A proposed forecast/budget change (mirrors assistant/api src/forecast/changes.ts). */
export interface ForecastChangePart {
  changeId: string;
  status: ChangeStatus;
  title: string;
  version: { id: number; name: string; year: number; versionType: 'budget' | 'forecast'; status: string; locationId?: number; locationName?: string };
  choice: { key: 'laborMode'; prompt: string; options: Array<{ id: string; label: string }> } | null;
  appliedVariant: string | null;
  variants: Array<{
    id: string;
    label: string | null;
    operations: Array<{ summary: string; warnings: string[]; skipped: Array<{ accountName?: string; reason: string }> }>;
    warnings: string[];
    impact: {
      totals: Record<'revenue' | 'expense' | 'net', ChangeTotals>;
      byDepartment: Array<ChangeTotals & { department: string; kind: 'revenue' | 'expense' }>;
      byMonth: Array<{ month: number; revenueDelta: number; expenseDelta: number }>;
      changedAccounts: number;
      knockOnAccounts: number;
      accounts: Array<ChangeTotals & { id: number; accountNumber: string; accountName: string; department: string | null; accountType: string; targeted: boolean }>;
    };
  }>;
  error: string | null;
  updatedAt: string;
}

/** A schedule the assistant drafted (mirrors assistant/api src/labor/schedule.ts). */
export interface ScheduleDailyLine {
  date: string;
  hours: number;
  forecastRooms: number | null;
  hpor: number | null;
  wages: number | null;
  forecastRevenue: number | null;
  laborPct: number | null;
  checkouts?: number | null;
  stayovers?: number | null;
}

export interface SchedulePart {
  id: string;
  title: string;
  propertyId: number;
  propertyName: string;
  demandSource: 'lodgeic' | 'rms' | null;
  daily: ScheduleDailyLine[];
  wageCoverage: number | null;
  dates: string[];
  rows: Array<{
    employeeId: number;
    name: string;
    position: string;
    cells: Record<string, Array<{ start: string; end: string; hours: number; position: string }>>;
    scheduledHours: number;
    priorHours: number;
    projectedOt: number;
  }>;
  open: Array<{ position: string; date: string; start: string; end: string; hours: number; note?: string }>;
  byPosition: Array<{ position: string; hours: Record<string, number>; total: number }>;
  totals: { employees: number; scheduledHours: number; openHours: number; projectedOt: number; recentWeeklyOt: number | null };
  notes: string[];
  warnings: string[];
}

/**
 * A governed workflow run (assistant/api src/agent/workflowTools.ts): the
 * POST /aiWorkflows/run response — exactly what the product's WorkflowRunCard
 * takes as initialRun — plus the workflow key and property name.
 */
export interface WorkflowPart {
  key: string;
  propertyName: string;
  workflowTitle?: string;
  run: { workflowRunId: string; orgId?: number; [k: string]: unknown };
  proposals: Array<{ id: string; state: string; [k: string]: unknown }>;
  /** From an imported v1 answer: only the run id is stored; the card loads the live run. */
  legacy?: boolean;
  [k: string]: unknown;
}

/**
 * A block of an answer imported from the v1 assistant (mirrors assistant/api
 * agent/protocol.ts LegacyPart): a fenced block (chart, signals, pnl_report,
 * forecast_changes…) or an artifact (data_table, pnl_report), rendered by the
 * v1 component in its original position among the answer's text.
 */
export interface LegacyPart {
  kind: string;
  source: 'fence' | 'artifact';
  placeholder: string;
  payload?: unknown;
  text?: string;
  lang?: string;
  propertyId?: number;
  omitted?: boolean;
}

export type AssistantDataParts = {
  plan: PlanPart;
  result: QueryResult;
  suggestions: { items: string[] };
  status: StatusPart;
  change: ForecastChangePart;
  schedule: SchedulePart;
  workflow: WorkflowPart;
  attachments: { items: AttachmentRef[] };
  /** On a user message: the page it was asked from. */
  context: AssistantPageContext;
  /** On a user message: the properties it was asked about (null = every one). */
  scope: { locationIds: number[] | null };
  rates: RatesPart;
  task: TaskApprovalPart;
  /** The answer after the server's claim check rewrote it; replaces the streamed text. */
  /** fixed: claim-check problems the rewrite fixed; linked: typed figures turned into citations. */
  correction: { text: string; fixed: number; linked?: number };
  /** On a user message: the request came from a scheduled task's run, not from typing. */
  scheduled: ScheduledRunPart;
  legacy: LegacyPart;
};

/** assistant/api agent/protocol.ts ScheduledRunPart. */
export interface ScheduledRunPart {
  taskId: string;
  taskName: string;
  runLabel: string | null;
  isTest: boolean;
}

/** A staged scheduled-task change (assistant/api agent/scheduleTools.ts), approved on the product's card. */
export interface TaskApprovalPart {
  id: string;
  pendingActionId: string;
  propertyName: string;
  title: string | null;
}

/** A live rate shop (assistant/api agent/rateTools.ts); uiArtifact is the product's data-table artifact. */
export interface RatesPart {
  id: string;
  propertyId: number;
  subjectHotel: string;
  uiArtifact: Record<string, unknown> | null;
  rows: Array<Record<string, unknown>>;
}

export type AssistantUIMessage = UIMessage<AssistantMetadata, AssistantDataParts>;

export interface ThreadSummary {
  id: string;
  organizationId: number;
  title: string;
  locationIds: number[] | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  messageCount: number;
  /** 'scheduled': a scheduled task's runs (one thread per task); the user can keep talking in it. */
  source?: 'user' | 'scheduled';
  /** The v1 threadId of a conversation imported from the v1 assistant. */
  legacyThreadId?: string | null;
  /** Pinned to the top of the list (null: not pinned). */
  pinnedAt?: string | null;
  /** An answer is still being written (it carries on with the page closed). */
  running?: boolean;
  /** A search result: the question that matched, trimmed around the match. */
  match?: string;
}

export interface AssistantContext {
  org: { id: number; name: string; currency: string };
  properties: Array<{ id: number; name: string }>;
  scope: number[];
  reportGroups: string[] | null;
  dataThrough: string | null;
  starters: string[];
  /** The starters grouped by area (newer services). */
  starterGroups?: Array<{ label: string; items: string[] }>;
}
